import { doc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import type { Chef } from '../types';
import type { InstagramOwner } from '../services/geminiService';
import { finalizeChefDraft } from './chefCsv';
import { extractInstagramUsername, getChefWebsites, isChefActive, normalizeInstagramUsername } from './chefDisplay';
import {
    chefNameLooksLikeRawInstagramFullName,
    profileFromInstagramOwner,
} from './instagramChefProfile';
import { ingestChefProfilePicUrl } from './chefMedia';

function instagramProfileUrl(username: string): string {
    return `https://www.instagram.com/${username}`;
}

async function enrichStoredChef(chef: Chef, username: string, owner: InstagramOwner): Promise<Chef> {
    const hasInstagram = getChefWebsites(chef).some((link) => extractInstagramUsername(link) === username);
    const websites = hasInstagram ? getChefWebsites(chef) : [...getChefWebsites(chef), instagramProfileUrl(username)];
    const updates: Record<string, unknown> = { updatedAt: new Date().toISOString() };
    let next = chef;

    if (!hasInstagram) {
        updates.websites = websites;
        updates.website = websites[0];
        next = { ...chef, websites, website: websites[0] };
    }
    if (owner.profilePicUrl && (!chef.imageUrl || !chef.imageUrl.includes('/media/read/'))) {
        const storedImageUrl = await ingestChefProfilePicUrl(chef.id, owner.profilePicUrl, chef.imageUrl);
        if (storedImageUrl) {
            updates.imageUrl = storedImageUrl;
            next = { ...next, imageUrl: storedImageUrl };
        }
    }
    if (!chef.instagramUsername) {
        updates.instagramUsername = username;
        next = { ...next, instagramUsername: username };
    }
    if (owner.userId && !chef.instagramUserId) {
        updates.instagramUserId = owner.userId;
        next = { ...next, instagramUserId: owner.userId };
    }

    if (owner.fullName?.trim()) {
        const profile = profileFromInstagramOwner(owner.fullName, username);
        if (profile.description && !chef.description?.trim()) {
            updates.description = profile.description;
            next = { ...next, description: profile.description };
        }
        if (
            profile.name &&
            chefNameLooksLikeRawInstagramFullName(chef.name, owner.fullName)
        ) {
            updates.name = profile.name;
            next = { ...next, name: profile.name };
        }
    }

    if (Object.keys(updates).length > 1) {
        await updateDoc(doc(db, 'chefs', chef.id), updates);
    }
    return next;
}

export async function resolveInstagramChef(
    owner: InstagramOwner,
    existingChefs: Chef[],
): Promise<Chef | null> {
    const username = normalizeInstagramUsername(owner.username);
    if (!username) return null;

    const existing = existingChefs.find(
        (chef) => isChefActive(chef) && (
            getChefWebsites(chef).some((link) => extractInstagramUsername(link) === username)
            || (owner.userId && chef.instagramUserId === owner.userId)
        ),
    );

    if (existing) {
        return enrichStoredChef(existing, username, owner);
    }

    const fullName = owner.fullName?.trim();
    const profile = profileFromInstagramOwner(fullName, username);
    const created = finalizeChefDraft({
        name: profile.name,
        englishName: username,
        instagramUsername: username,
        ...(owner.userId ? { instagramUserId: owner.userId } : {}),
        ...(profile.description ? { description: profile.description } : {}),
        websites: [instagramProfileUrl(username)],
    });
    if (!created) return null;

    if (owner.profilePicUrl) {
        const storedImageUrl = await ingestChefProfilePicUrl(created.id, owner.profilePicUrl);
        if (storedImageUrl) {
            created.imageUrl = storedImageUrl;
        } else {
            created.imageUrl = owner.profilePicUrl;
        }
    }

    const alreadyStored = existingChefs.find((chef) => chef.id === created.id);
    if (alreadyStored) {
        return enrichStoredChef(alreadyStored, username, owner);
    }

    await setDoc(doc(db, 'chefs', created.id), created);
    return created;
}
